import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MDEditor from '@uiw/react-md-editor';
import { PrivacyPolicy, CollectionNotice } from '@/services/policyService';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';

interface PrivacyPolicyDialogProps {
  open: boolean;
  onClose: () => void;
  policy: PrivacyPolicy | CollectionNotice | null;
  draft: string;
  setDraft: (v: string) => void;
  mode: 'view' | 'edit';
  setMode: (m: 'view' | 'edit') => void;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  initialTab?: 'view' | 'history'; // Optional: allow opening on history tab
  title?: string;
}

import { useEffect } from 'react';

const PrivacyPolicyDialog: React.FC<PrivacyPolicyDialogProps> = ({
  open, onClose, policy, draft, setDraft, mode, setMode, loading, saving, error, onSave, initialTab = 'view', title = "Privacy Policy"
}) => {
  // Ensure draft is always up to date when entering edit mode
  useEffect(() => {
    if (open && mode === 'edit') {
      setDraft(decodeURIComponent(policy?.content || '') || '');
    }
    // Only run when open/mode change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);
  const isMobile = useIsMobile();
  const { isDark } = useTheme();

  // Tab state (reset on open)
  const [tab, setTab] = useState<'view' | 'history'>(initialTab);
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const renderViewTab = () => (
    mode === 'edit' ? (
      <div className="space-y-4">
        <MDEditor
          value={draft}
          height={isMobile ? '100vh' : '80vh'}
          style={!isMobile ? { minHeight: 400, maxHeight: '100vh'} : {}}
          onChange={v => setDraft(v || '')}
          data-color-mode={isDark ? 'dark' : 'light'}
        />
      </div>
    ) : (
      <div className="prose max-w-none bg-transparent p-4 rounded border">
        <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]}>
          {policy?.content ? decodeURIComponent(policy.content) : `*No ${title.toLowerCase()} found.*`}
        </ReactMarkdown>
        <div className="text-xs text-gray-500 mt-2">
          Version: {policy?.version} | Last Edited: {policy?.lastEdited?.toLocaleString?.()}
        </div>
      </div>
    )
  );

  const renderHistoryTab = () => (
    <div className="bg-transparent p-4 rounded border">
      {policy?.editHistory && policy.editHistory.length > 0 ? (
        <ul className="space-y-4">
          {[...policy.editHistory]
          .sort((a, b) => {
            // Prefer editedAt if present, else fallback to previousVersion string comparison
            if (a.editedAt && b.editedAt) {
              return (b.editedAt?.getTime?.() || 0) - (a.editedAt?.getTime?.() || 0);
            }
            if (a.previousVersion && b.previousVersion) {
              // Try semantic version compare, fallback to string
              try {
                const av = a.previousVersion.split('.').map(Number);
                const bv = b.previousVersion.split('.').map(Number);
                for (let i = 0; i < Math.max(av.length, bv.length); i++) {
                  if ((bv[i] || 0) !== (av[i] || 0)) return (bv[i] || 0) - (av[i] || 0);
                }
                return 0;
              } catch {
                return b.previousVersion.localeCompare(a.previousVersion);
              }
            }
            return 0;
          })
          .map((h, i) => (
          <li key={i} className="border-b pb-2">
              <div className="font-semibold text-sm">Version {h.previousVersion}</div>
              <div className="text-xs text-gray-500">
                Edited by {h.editedBy} at {h.editedAt?.toLocaleString?.()}
              </div>
              <details className="mt-1">
                <summary className="cursor-pointer text-xs underline">Show previous version</summary>
                <div className="prose-sm bg-transparent p-2 rounded mt-1">
                  <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]}>
                    {h.previousContent ? decodeURIComponent(h.previousContent) : ''}
                  </ReactMarkdown>
                </div>
              </details>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-gray-400">No edit history available.</div>
      )}
    </div>
  );

  const tabHeader = (
    <TabsList>
      <TabsTrigger value="view" onClick={() => setTab('view')}>View</TabsTrigger>
      <TabsTrigger value="history" onClick={() => setTab('history')}>History</TabsTrigger>
    </TabsList>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onClose}>
        <DrawerContent className="w-full max-w-full p-0 bg-card">
          <DrawerHeader>
           <div className="flex flex-row gap-2 justify-between mt-4">
            <DrawerTitle>{title}</DrawerTitle>
            <div className="flex flex-row gap-2 justify-end">
              {mode === 'edit' && (
                <div className="flex flex-row gap-2 justify-end">
                  {/* <Button variant="outline" onClick={() => { setDraft(policy?.content || ''); setMode('view'); }}>
                    Cancel
                  </Button> */}
                  <Button onClick={onSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
          </DrawerHeader>
          <div className="mt-4">{tabHeader}</div>
          <div className="p-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
            <Tabs value={tab} onValueChange={v => setTab(v as 'view' | 'history')}>
              <TabsContent value="view">{renderViewTab()}</TabsContent>
              <TabsContent value="history">{renderHistoryTab()}</TabsContent>
            </Tabs>
          </div>
          <DrawerFooter />
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onClose} jira-taskid="DED-1231">
      <DialogContent className="w-full max-w-screen-lg sm:max-w-screen-lg max-h-[90vh] bg-card flex flex-col">
        <DialogHeader>
          <div className="flex flex-row gap-2 justify-between mt-4">
            <DialogTitle>{title}</DialogTitle>
            <div className="flex flex-row gap-2 justify-end">
              {mode === 'edit' && (
                <div className="flex flex-row gap-2 justify-end">
                  {/* <Button variant="outline" onClick={() => { setDraft(policy?.content || ''); setMode('view'); }}>
                    Cancel
                  </Button> */}
                  <Button onClick={onSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Tabs value={tab} onValueChange={v => setTab(v as 'view' | 'history')}>
            <TabsContent value="view">{renderViewTab()}</TabsContent>
            <TabsContent value="history">{renderHistoryTab()}</TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;
